import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, Grid, Stack, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, MenuItem,
    Avatar, InputAdornment
} from '@mui/material';
import { AccountBalanceWallet, AddCircle, History, TrendingUp, TrendingDown } from '@mui/icons-material';
import { collection, addDoc, getDocs, getDoc, query, where, orderBy, doc, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from 'sweetalert2';

const PRIMARY = "#0d9488"; // Teal 600
const SECONDARY = "#0f172a"; // Slate 900

function Studentwallet() {
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [history, setHistory] = useState([]);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [schoolId, setSchoolId] = useState(null);

    const [formData, setFormData] = useState({
        amount: '',
        description: 'Wallet Top-up',
        payMode: 'Cash'
    });

    useEffect(() => {
        const init = async () => {
            const userDoc = await getDoc(doc(db, "users", auth.currentUser?.uid));
            const sId = userDoc.data()?.schoolId;
            const yearSnap = await getDocs(query(collection(db, "academicyears"), where("schoolId", "==", sId), where("status", "==", "Active")));
            const activeYear = yearSnap.empty ? null : yearSnap.docs[0].data().name;
            if (sId && activeYear) fetchData(sId, activeYear);
        };
        init();
    }, []);

    const fetchData = async (sId, year) => {
        try {
            setLoading(true);
            const [courseSnap, admSnap, stdSnap] = await Promise.all([
                getDocs(query(collection(db, "courses"), orderBy("sequence", "asc"))),
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("academicYear", "==", year), where('status', "==", 'Active'))),
                getDocs(collection(db, "students"))
            ]);
            const courseMap = {};
            courseSnap.forEach(d => { courseMap[d.id] = d.data().name; });
            const admMap = {};
            admSnap.forEach(d => { admMap[d.data().studentId] = d.data(); });

            const merged = stdSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(s => admMap[s.id])
                .map(s => ({
                    ...s,
                    label: `${s.firstname} ${s.fathername} ${s.surname}  (${courseMap[admMap[s.id].class] || ''})`.toUpperCase()
                }));
            setStudents(merged);
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };

    const fetchWalletHistory = async (student) => {
        if (!student) return;
        setLoading(true);
        try {
            const q = query(collection(db, "wallet_transactions"), where("studentId", "==", student.id), orderBy("timestamp", "desc"));
            const snap = await getDocs(q);
            const txns = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const total = txns.reduce((acc, curr) => curr.type === 'Credit' ? acc + Number(curr.amount) : acc - Number(curr.amount), 0);
            setHistory(txns);
            setBalance(total);
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };

    const handleAddCredit = async () => {
        if (loading || !formData.amount || !selectedStudent) return;

        setLoading(true); // Start loading immediately
        try {
            const txn = {
                studentId: selectedStudent.id,
                studentName: selectedStudent.label,
                schoolId,
                amount: Number(formData.amount),
                type: 'Credit',
                description: formData.description,
                payMode: formData.payMode,
                timestamp: Timestamp.now(),
                dateTime: new Date().toLocaleString()
            };

            await addDoc(collection(db, "wallet_transactions"), txn);

            // Reset states
            setOpenAdd(false);
            setFormData({ amount: '', description: 'Wallet Top-up', payMode: 'Cash' });

            await Swal.fire({
                icon: 'success',
                title: 'Funds Added',
                text: `₹${txn.amount} credited successfully`,
                timer: 2000,
                showConfirmButton: false
            });

            // Refresh data
            fetchWalletHistory(selectedStudent);
        } catch (e) {
            Swal.fire("Error", e.message, "error");
        } finally {
            setLoading(false); // Stop loading regardless of success or failure
        }
    };

    return (
        <Box p={2} sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>
            {/* Compact Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: SECONDARY, letterSpacing: '-0.5px' }}>
                    Student <span style={{ color: PRIMARY }}>Wallet</span>
                </Typography>
                <Autocomplete
                    size="small"
                    options={students}
                    sx={{ width: 300, bgcolor: 'white', borderRadius: '8px' }}
                    getOptionLabel={(o) => o.label}
                    onChange={(e, v) => { setSelectedStudent(v); fetchWalletHistory(v); }}
                    renderInput={(params) => <TextField {...params} placeholder="Search Student..." variant="outlined" />}
                />
            </Stack>

            <Grid className="row" spacing={2}>
                {/* Balance Card Section */}
                {selectedStudent && (
                    <Grid className="col-sm-4" item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 2,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: `linear-gradient(135deg, ${SECONDARY} 0%, #1e293b 100%)`,
                            color: 'white'
                        }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.1)', width: 56, height: 56 }}>
                                    <AccountBalanceWallet sx={{ color: PRIMARY, fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                        Current Balance
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900 }}>₹{balance.toLocaleString()}</Typography>
                                </Box>
                            </Stack>
                            <Button
                                variant="contained"
                                startIcon={<AddCircle />}
                                sx={{ bgcolor: PRIMARY, borderRadius: '10px', px: 3, '&:hover': { bgcolor: '#0b7a6f' } }}
                                onClick={() => setOpenAdd(true)}
                            >
                                Add Credit
                            </Button>
                        </Paper>
                    </Grid>
                )}

                {/* History Table Section */}
                <Grid className="col-sm-8" item xs={12}>
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <Box p={2} borderBottom="1px solid #f1f5f9">
                            <Typography variant="subtitle2" fontWeight="700">Transaction History</Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: '60vh' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>Date/Time</TableCell>
                                        <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>Details</TableCell>
                                        <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>Mode</TableCell>
                                        <TableCell align="right" sx={{ color: '#64748b', fontWeight: 600 }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{row.dateTime}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {row.type === 'Credit' ? <TrendingUp sx={{ color: 'green', fontSize: 16 }} /> : <TrendingDown sx={{ color: 'red', fontSize: 16 }} />}
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="600">{row.description}</Typography>
                                                        <Typography variant="caption" color="textSecondary">{row.type}</Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell><Chip size="small" label={row.payMode || 'Auto'} variant="outlined" sx={{ fontSize: '10px' }} /></TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: row.type === 'Credit' ? '#059669' : '#e11d48' }}>
                                                    {row.type === 'Credit' ? '+' : '-'} ₹{row.amount}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!selectedStudent && (
                                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 8 }}>Please select a student to view wallet</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Modern Add Credit Popup */}
            <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                    <Typography variant="h6" fontWeight="bold">Top-up Wallet</Typography>
                    <Typography variant="caption" color="textSecondary">{selectedStudent?.label}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} mt={1}>
                        <TextField
                            fullWidth
                            label="Recharge Amount"
                            type="number"
                            autoFocus
                            InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        />
                        <TextField
                            select fullWidth label="Payment Mode" size="small"
                            value={formData.payMode}
                            onChange={(e) => setFormData({ ...formData, payMode: e.target.value })}
                        >
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="Online">Online / UPI</MenuItem>
                            <MenuItem value="Cheque">Cheque</MenuItem>
                        </TextField>
                        <TextField
                            fullWidth label="Remark" multiline rows={2} size="small"
                            placeholder="e.g. Received from Father"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={() => setOpenAdd(false)} fullWidth sx={{ color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={loading} // This is the most important part
                        sx={{
                            bgcolor: PRIMARY,
                            height: '45px',
                            borderRadius: '12px',
                            '&:disabled': { bgcolor: '#94a3b8', color: 'white' } // Optional styling for disabled state
                        }}
                        onClick={handleAddCredit}
                    >
                        {loading ? 'Processing...' : 'Confirm Recharge'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Studentwallet;